/**
 * New-UX "Timeline items" table (list-icon sidebar section). Replaces
 * TimelineItemStatusList's card-per-item rendering with a compact one-row-per-item
 * table + a separate TimelineItemDetailsPanel — reuses only the underlying item data
 * (ItemData) and status logic, not Task.tsx/Automation.tsx/Message.tsx's card JSX.
 */
import { Doc } from '@dadohr/shared/domainTypes/document';
import { IExperience, IExperienceTemplate } from '@dadohr/shared/domainTypes/experience';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { CheckCircle2, Mail, Users, Zap } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import {
  ChipFilterWithDropdown,
  matchesExclusiveAllFilter
} from '../../../../../components/ChipFilterWithDropdown';
import { DateTimeHoverTooltip } from '../../../../../components/DateTimeTooltip';
import { useUnsafeTranslation } from '../../../../../i18next';
import { toMoment } from '../../../../../services/time';
import { ItemData } from '../../../../../services/participatDialog/participantDialogHelpers';
import { ACTOR_COLORS_LIGHT, ActorDescriptor } from '../../../components/Actors';
import {
  ActorChip,
  FilterButton,
  SearchInput,
  SortButton,
  StatusChip,
  TextLinkButton
} from '../../../components/Builders/newux/kit/controls';
import { getFilterButtonState } from '../../../components/Builders/newux/kit/filterButtonState';
import { tokens } from '../../../components/Builders/newux/kit/tokens';
import { getItemDisplayName, getItemDisplayPair } from '../../../helpers/adminName';
import { Selections } from '../TimelineItemStatusList';
import {
  getItemActualDate,
  getItemDate,
  getItemDateRange,
  getItemScheduledDate,
  getItemStatusCategory,
  getItemStatusChipConfig,
  getMessageErrorState,
  getTaskErrorState,
  getWriteFailedSubtaskTplIds,
  getTriggerDescription,
  getVisibleAudienceName,
  isItemLate,
  StatusCategory,
  TaskErrorState
} from './statusHelpers';
import { groupConcreteMessages } from '../../../../../services/experiences/instance';

const { color, font, radius } = tokens;

const Container = styled('div')`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid ${color.borderSubtle};
`;

/**
 * Sticky title + filter bar. Body (the dialog's single page-level scrollport) is the
 * nearest scrolling ancestor, so this sticks relative to the whole page rather than to
 * some inner container — it stays visible as you scroll past the timeline above it too,
 * not just while scrolling the row list below it.
 */
const StickyHeader = styled('div')`
  position: sticky;
  top: 0;
  z-index: 2;
  background: ${color.white};
  border-top: 1px solid ${color.borderSubtle};
  padding: ${tokens.space(5)} ${tokens.space(5)} 0;
`;

const RowsContainer = styled('div')`
  padding: 0 ${tokens.space(5)} ${tokens.space(5)};
`;

const Title = styled('div')`
  font-size: ${font.variant.h5.fontSize}px;
  line-height: ${font.variant.h5.lineHeight}px;
  font-weight: ${font.variant.h5.fontWeight};
  margin-bottom: ${tokens.space(4)};
`;

const FilterBar = styled('div')`
  display: flex;
  align-items: center;
  gap: ${tokens.space(2)};
  margin-bottom: ${tokens.space(4)};
`;

const SearchBoxSlot = styled(SearchInput)`
  margin-left: auto;
`;

const Row = styled('div')<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${tokens.space(3)};
  padding: ${tokens.space(2)} ${tokens.space(2)};
  border-radius: ${radius.md}px;
  cursor: pointer;
  background: ${(p) => (p.selected ? p.theme.custom.colors.blue.w100 : 'transparent')};
  &:hover {
    background: ${(p) => (p.selected ? p.theme.custom.colors.blue.w100 : color.white)};
  }
`;

const RowCheckbox = styled('input')`
  width: 15px;
  height: 15px;
`;

const RowTitle = styled('div')`
  flex: 1 1 auto;
  min-width: 0;
  font-size: ${font.variant.body2.fontSize}px;
  color: ${color.textPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowTitlePrimary = styled('span')`
  font-weight: ${font.weightScale.medium};
`;

const RowTitleSecondary = styled('span')`
  font-style: italic;
  font-weight: ${font.weightScale.regular};
  margin-left: ${tokens.space(2)};
`;

const RowAudience = styled('span')`
  margin-left: ${tokens.space(3)};
  color: ${color.textSecondary};
  font-size: ${font.variant.caption.fontSize}px;
  & svg {
    width: 12px;
    height: 12px;
    margin-right: 4px;
    vertical-align: middle;
    position: relative;
    top: -1px;
  }
`;

const ChipDateGroup = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
`;

const RowDate = styled('div')`
  width: 90px;
  flex: 0 0 auto;
  text-align: right;
  font-size: ${font.variant.caption.fontSize}px;
  color: ${color.textSecondary};
  white-space: nowrap;
`;

const ITEM_TYPE_ICON: Record<ItemData['type'], React.ReactNode> = {
  MESSAGE: <Mail />,
  TASK: <CheckCircle2 />,
  AUTOMATION: <Zap />
};

const itemKey = (item: ItemData) =>
  `${item.type}-${item.type === 'AUTOMATION' ? item.data.templateId : item.data.templateId}`;

const getItemActorKey = (item: ItemData): string =>
  item.type === 'AUTOMATION' ? 'AUTOMATION' : item.actor.key;

const isItemSelected = (item: ItemData, selections: Selections): boolean => {
  if (item.type === 'TASK') return !!selections.tasks[item.data.templateId];
  if (item.type === 'MESSAGE') return !!selections.messages[item.data.templateId];
  return !!selections.automations[item.data.templateId];
};

const toggleItem = (
  item: ItemData,
  setSelections: React.Dispatch<React.SetStateAction<Selections>>
) => {
  setSelections((s) => {
    if (item.type === 'TASK') {
      return {
        ...s,
        tasks: {
          ...s.tasks,
          [item.data.templateId]: !s.tasks[item.data.templateId]
        }
      };
    }
    if (item.type === 'MESSAGE') {
      return {
        ...s,
        messages: {
          ...s.messages,
          [item.data.templateId]: !s.messages[item.data.templateId]
        }
      };
    }
    return {
      ...s,
      automations: {
        ...s.automations,
        [item.data.templateId]: !s.automations[item.data.templateId]
      }
    };
  });
};

export const TimelineItemsTable: React.FC<{
  tpl: Doc<IExperienceTemplate>;
  d: Doc<IExperience>;
  timelineItems: ItemData[];
  selections: Selections;
  setSelections: React.Dispatch<React.SetStateAction<Selections>>;
  actorDescriptors: { [key: string]: ActorDescriptor };
}> = ({ tpl, d, timelineItems, selections, setSelections, actorDescriptors }) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();

  const [typeFilters, setTypeFilters] = useState<ItemData['type'][]>([]);
  const [actorFilters, setActorFilters] = useState<string[]>([]);
  const [statusFilters, setStatusFilters] = useState<StatusCategory[]>([]);
  const [q, setQ] = useState('');
  const [sortDesc, setSortDesc] = useState(false);

  const groupedConcreteMessages = useMemo(
    () => groupConcreteMessages(d.data.messages),
    [d.data.messages]
  );
  const concreteMessagesByTask = groupedConcreteMessages.byTask;
  const concreteMessagesByMessage = groupedConcreteMessages.byMessage;

  // Computed once per task (not once per task per render, or twice per task per render
  // between filtering and row rendering) and looked up by both below.
  const writeFailedSubtaskTplIds = useMemo(
    () => getWriteFailedSubtaskTplIds(d.data.instanceErrors),
    [d.data.instanceErrors]
  );
  const taskErrorStateByKey = useMemo(() => {
    const m = new Map<string, TaskErrorState>();
    timelineItems.forEach((item) => {
      if (item.type === 'TASK') {
        m.set(
          itemKey(item),
          getTaskErrorState(item.data, concreteMessagesByTask, undefined, writeFailedSubtaskTplIds)
        );
      }
    });
    return m;
  }, [timelineItems, concreteMessagesByTask, writeFailedSubtaskTplIds]);
  const getCachedTaskErrorState = (item: ItemData): TaskErrorState =>
    item.type === 'TASK' ? taskErrorStateByKey.get(itemKey(item)) ?? null : null;

  // Same idea for messages: a message the backend marked DONE can still have an
  // errored channel (e.g. email succeeded, Slack failed) — see getMessageErrorState.
  const messageErrorStateByKey = useMemo(() => {
    const m = new Map<string, TaskErrorState>();
    timelineItems.forEach((item) => {
      if (item.type === 'MESSAGE') {
        m.set(
          itemKey(item),
          getMessageErrorState(item.data.templateId, item.data.status, concreteMessagesByMessage)
        );
      }
    });
    return m;
  }, [timelineItems, concreteMessagesByMessage]);
  const getCachedMessageErrorState = (item: ItemData): TaskErrorState =>
    item.type === 'MESSAGE' ? messageErrorStateByKey.get(itemKey(item)) ?? null : null;

  const itemTypeOptions: { key: ItemData['type']; label: string }[] = [
    { key: 'MESSAGE', label: t('Messages') },
    { key: 'TASK', label: t('Tasks') },
    { key: 'AUTOMATION', label: t('Automations') }
  ];
  const {
    selectedOptions: selectedTypeOptions,
    active: typeFilterActive,
    label: typeButtonLabel
  } = getFilterButtonState(itemTypeOptions, typeFilters, t('Item type'), (count) =>
    t('{{count}} types', { count })
  );

  const actorOptions: { key: string; label: string }[] = [
    ...Object.values(actorDescriptors).map((a) => ({ key: a.key, label: a.label })),
    { key: 'AUTOMATION', label: t('Automation') }
  ];
  const {
    selectedOptions: selectedActorOptions,
    active: actorFilterActive,
    label: actorButtonLabel
  } = getFilterButtonState(actorOptions, actorFilters, t('Actor'), (count) =>
    t('{{count}} actors', { count })
  );

  const statusOptions: { key: StatusCategory; label: string }[] = [
    { key: 'UPCOMING', label: t('Upcoming') },
    { key: 'ACTIONABLE', label: t('To do') },
    { key: 'DONE', label: t('Done') },
    { key: 'ERROR', label: t('Error') },
    { key: 'SKIPPED', label: t('Skipped') }
  ];
  const {
    selectedOptions: selectedStatusOptions,
    active: statusFilterActive,
    label: statusButtonLabel
  } = getFilterButtonState(statusOptions, statusFilters, t('Status'), (count) =>
    t('{{count}} statuses', { count })
  );

  const anyFilterActive = typeFilterActive || actorFilterActive || statusFilterActive;
  const resetAllFilters = () => {
    setTypeFilters([]);
    setActorFilters([]);
    setStatusFilters([]);
  };

  const filtered = useMemo(() => {
    // `timelineItems` arrives pre-sorted by _mapFn's `orderBy` (participatDialogHelpers.ts)
    // — a multi-key sort that's `triggeredBy`-aware (a triggered item sorts by its parent's
    // resolved date, not its own placeholder start) and has a deliberate `Infinity`
    // fallback for undated automations. Don't re-derive a naive date sort on top of that;
    // just trust the incoming order and reverse it for "Newest first".
    let xs = timelineItems;
    xs = xs.filter((x) => matchesExclusiveAllFilter(typeFilters, x.type));
    xs = xs.filter((x) => matchesExclusiveAllFilter(actorFilters, getItemActorKey(x)));
    xs = xs.filter((x) =>
      matchesExclusiveAllFilter(
        statusFilters,
        getItemStatusCategory(x, getCachedTaskErrorState(x), getCachedMessageErrorState(x))
      )
    );
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      xs = xs.filter((x) =>
        getItemDisplayName(x.data).toLowerCase().includes(needle)
      );
    }
    return sortDesc ? [...xs].reverse() : xs;
  }, [
    timelineItems,
    typeFilters,
    actorFilters,
    statusFilters,
    taskErrorStateByKey,
    messageErrorStateByKey,
    q,
    sortDesc
  ]);

  return (
    <Container>
      <StickyHeader>
      <Title>{t('Timeline items')}</Title>
      <FilterBar>
        <SortButton
          descending={sortDesc}
          onClick={() => setSortDesc((x) => !x)}
          label={t('Date')}
        />
        <ChipFilterWithDropdown
          label={t('Actor')}
          selectedOptions={selectedActorOptions}
          options={actorOptions}
          onChange={(xs) => setActorFilters(xs.map((x) => x.key))}
          exclusiveAll
          renderTrigger={({ onClick }) => (
            <FilterButton label={actorButtonLabel} active={actorFilterActive} onClick={onClick} />
          )}
        />
        <ChipFilterWithDropdown
          label={t('Status')}
          selectedOptions={selectedStatusOptions}
          options={statusOptions}
          onChange={(xs) => setStatusFilters(xs.map((x) => x.key))}
          exclusiveAll
          renderTrigger={({ onClick }) => (
            <FilterButton
              label={statusButtonLabel}
              active={statusFilterActive}
              onClick={onClick}
            />
          )}
        />
        <ChipFilterWithDropdown
          label={t('Item type')}
          selectedOptions={selectedTypeOptions}
          options={itemTypeOptions}
          onChange={(xs) => setTypeFilters(xs.map((x) => x.key))}
          exclusiveAll
          renderTrigger={({ onClick }) => (
            <FilterButton label={typeButtonLabel} active={typeFilterActive} onClick={onClick} />
          )}
        />
        {anyFilterActive && (
          <TextLinkButton label={t('Reset filters')} onClick={resetAllFilters} />
        )}
        <SearchBoxSlot value={q} onChange={setQ} placeholder={t('Search items')} />
      </FilterBar>
      </StickyHeader>

      <RowsContainer>
      {filtered.map((item) => {
        const displayPair = getItemDisplayPair(item.data);
        const actorLabel =
          item.type === 'AUTOMATION' ? t('Automation') : item.actor.label;
        const actorColor =
          item.type === 'AUTOMATION'
            ? theme.custom.colors.purple.w300
            : item.actor.color;
        const taskErrorState = getCachedTaskErrorState(item);
        const messageErrorState = getCachedMessageErrorState(item);
        // Automation's badge color is a fixed kit accent, not an assigned actor color, so
        // it's never "legacy" — only a real actor's own color can predate the v4 palette.
        const isLegacyActorColor =
          item.type !== 'AUTOMATION' &&
          !ACTOR_COLORS_LIGHT.some((c) => c.hex.toLowerCase() === actorColor.toLowerCase());
        const [statusLabel, statusStyle] = getItemStatusChipConfig(
          item,
          t,
          taskErrorState,
          messageErrorState
        );
        const category = getItemStatusCategory(item, taskErrorState, messageErrorState);
        const actualDate = getItemActualDate(
          item,
          concreteMessagesByTask,
          concreteMessagesByMessage
        );
        const late = isItemLate(getItemScheduledDate(item), actualDate);
        const date = late && actualDate ? actualDate : getItemDate(item);
        const range = getItemDateRange(item);
        const trigger = getTriggerDescription(item, t);
        const visibleAudienceName = getVisibleAudienceName(item);
        const selected = isItemSelected(item, selections);
        return (
          <Row
            key={itemKey(item)}
            selected={selected}
            onClick={() => toggleItem(item, setSelections)}
          >
            <RowCheckbox
              type="checkbox"
              checked={selected}
              onChange={() => toggleItem(item, setSelections)}
              onClick={(e) => e.stopPropagation()}
            />
            <ActorChip
              color={actorColor}
              icon={ITEM_TYPE_ICON[item.type]}
              dimmed={isLegacyActorColor}
            >
              {actorLabel}
            </ActorChip>
            <RowTitle>
              <RowTitlePrimary>{displayPair.primary}</RowTitlePrimary>
              {displayPair.secondary && (
                <RowTitleSecondary>{displayPair.secondary}</RowTitleSecondary>
              )}
              {visibleAudienceName && (
                <RowAudience
                  title={t('This {{ type }} uses the "{{ audience }}" audience', {
                    type: t(item.type === 'AUTOMATION' ? 'automation' : item.type.toLowerCase()),
                    audience: visibleAudienceName
                  })}
                >
                  <Users />{visibleAudienceName}
                </RowAudience>
              )}
            </RowTitle>
            <ChipDateGroup>
              <StatusChip tone={statusStyle.tone} variant={statusStyle.variant}>
                {statusLabel}
              </StatusChip>
              <RowDate>
                {trigger ? (
                  trigger.short
                ) : category === 'SKIPPED' ? (
                  t('N/A')
                ) : item.type === 'TASK' ? (
                  range ? (
                    <DateTimeHoverTooltip at={range.start} rangeEnd={range.end}>
                      {`${toMoment(range.start).tz(tpl.data.tz).format('D MMM')}–${toMoment(range.end).tz(tpl.data.tz).format('D MMM')}`}
                    </DateTimeHoverTooltip>
                  ) : (
                    t('N/A')
                  )
                ) : date ? (
                  <DateTimeHoverTooltip at={date}>
                    {`${
                      item.type === 'MESSAGE'
                        ? category === 'DONE'
                          ? t('Sent')
                          : category === 'ERROR'
                          ? t('Failed')
                          : t('Sends')
                        : category === 'DONE'
                        ? t('Ran')
                        : category === 'ERROR'
                        ? t('Failed')
                        : t('Runs')
                    } ${toMoment(date).tz(tpl.data.tz).format('D MMM')}`}
                  </DateTimeHoverTooltip>
                ) : (
                  t('N/A')
                )}
              </RowDate>
            </ChipDateGroup>
          </Row>
        );
      })}
      </RowsContainer>
    </Container>
  );
};
